import { Dictionary } from '@key-telematics/fleet-api-client';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { ListFieldValue } from 'app/shared/components/form-builder/list/list.component';
import { ThemeEngine } from 'app/shared/components/theme';
import { get } from 'lodash';
import { ENTITY_HANDLER_PROVIDERS } from '..';
import { AdminEditorService } from '../../admin.service';
import { ComboField, NumberField, TextField } from '../utils';


interface ListItem {
    title: string;
    url?: string;
    color?: string;
    icon: string;
    iconFill?: boolean,
}


export abstract class FeatureFlagListHandler<TlistItem extends ListItem> {

    constructor(public admin: AdminEditorService, public translationKey: string) {
    }

    abstract decodeEntryString(str: string): TlistItem;
    abstract encodeEntryString(item: TlistItem): string;
    abstract getModalFields(): FormBuilderField[];

    mapEntityToEditor(key: string, values: any) {
        const item = values[key];
        values[key] = (Array.isArray(item) ? item : item?.split('\n'))?.map(item => {
            try {
                if (typeof item === 'string') {
                    if (item) {
                        return this.toListFieldItem(this.decodeEntryString(item || ''));
                    }
                } else {
                    return item;
                }
            } catch (err) {
                console.log(item);
                console.log(err);
            }
        }).filter(x => x);
    }

    mapEditorToEntity(key: string, values: any) {
        let items = values[key];
        if (Array.isArray(items)) {
            items = items.map(item => this.encodeEntryString(item.data)).join('\n');
        }
        values[key] = items;
    }

    translate(key: string, values?: Object): string {
        return this.admin.i18n.instant(`FEATURES.LISTS.${key}`, values);
    }

    toListFieldItem(data: TlistItem): ListFieldValue<TlistItem> {
        return {
            id: data.title,
            name: data.title,
            icon: data.icon || 'browser',
            description: data.url,
            data: data,
            color: data?.color,
            iconFill: data.iconFill
        };
    }

    init(field: FormBuilderField) {

        field.actions = [
            { name: this.admin.i18n.instant('SHARED.ADD'), click: this.showModal.bind(this), },
            { id: 'edit', click: this.showModal.bind(this), },
        ];
        field.getText = (f: FormBuilderField, values: any) => {
            if (!values || !values[f.id]) { return ''; }
            return values[f.id].map(x => x.name).join('<br/>');
        };

    }

    getDefault(): TlistItem {
        return {
            title: '',
            url: '',
            icon: '',
        } as any;
    }


    showModal(_action: string, field: FormBuilderField, values: any, item: ListFieldValue<TlistItem>): Promise<TlistItem> {

        return this.admin.showFormModal<TlistItem>({
            groups: [{
                name: this.translate(`MODALS.${this.translationKey}.TITLE`),
                description: this.translate(`MODALS.${this.translationKey}.DESC`),
                fields: this.getModalFields(),
            }],
        }, (item?.data) || this.getDefault()
        ).then(result => {
            if (result) {
                values[field.id] = this.updateOrAddItem(values[field.id], item?.id, result);
            }
            return result;
        });


    }

    updateOrAddItem(items: ListFieldValue<TlistItem>[], existingId: string, newItem: TlistItem): ListFieldValue<TlistItem>[] {
        const idx = items.findIndex(x => x.id === existingId);
        if (idx === -1) {
            items.push(this.toListFieldItem(newItem));
        } else {
            items[idx] = this.toListFieldItem(newItem);
        }
        return items;
    }

}

interface ExternalTabListItem extends ListItem {
    section: string;
    order: number;
}

export class ExternalTabsHandler extends FeatureFlagListHandler<ExternalTabListItem> {

    constructor(admin: AdminEditorService) {
        super(admin, 'EXTERNAL-TABS');
    }

    getDefault(): ExternalTabListItem {
        return {
            title: '',
            url: '',
            icon: 'external',
            section: '',
            order: 0
        };
    }

    decodeEntryString(str: string): ExternalTabListItem {
        const idx = str.indexOf(':');

        const info = str.substr(0, idx);
        const [title, icon, section, order] = info.split('|');

        const url = str.substr(idx + 1);
        return {
            title,
            icon: icon || 'browser',
            url: url,
            section: section || '',
            order: Number(order) || 0,
        };
    }

    encodeEntryString(item: ExternalTabListItem): string {
        const sanitize = (str: string): string => {
            return str.replace(/:|\|/g, '');
        };
        return `${sanitize(item.title)}|${sanitize(item.icon)}|${sanitize(item.section)}|${item.order}:${item.url}`;
    }

    getModalFields(): FormBuilderField[] {
        return [
            TextField('title', this.translate('FIELDS.TITLE'), true, 1, 20),
            TextField('url', this.translate('FIELDS.URL'), true, 1, 255),
            TextField('icon', this.translate('FIELDS.ICON'), false, 1, 20),
            { ...TextField('section', this.translate('FIELDS.SECTION'), false, 1, 20), description: this.translate('FIELDS.SECTION_DESC') },
            NumberField('order', this.translate('FIELDS.ORDER'), false, 1, 1000, undefined)
        ];
    }


}

export class OverviewTabsHandler extends FeatureFlagListHandler<ListItem> {

    constructor(admin: AdminEditorService) {
        super(admin, 'OVERVIEW-TABS');
    }


    decodeEntryString(str: string): ListItem {
        const idx = str.indexOf(':');
        const title = str.substr(0, idx);
        const url = str.substr(idx + 1);
        const icon = 'browser';
        return {
            title,
            icon,
            url,
        };
    }

    encodeEntryString(item: ListItem): string {
        const sanitize = (str: string): string => {
            return str.replace(/:|\|/g, '');
        };
        return `${sanitize(item.title)}:${item.url}`;
    }

    getModalFields(): FormBuilderField[] {
        return [
            TextField('title', this.translate('FIELDS.TITLE'), true, 1, 20),
            TextField('url', this.translate('FIELDS.URL'), true, 1, 255),
        ];
    }

}

interface AdminEntityListItem extends ListItem {
    entity: string;
}

export class AdminEntityTabsHandler extends FeatureFlagListHandler<AdminEntityListItem> {

    constructor(admin: AdminEditorService) {
        super(admin, 'ADMIN-ENTITY-TABS');
    }


    decodeEntryString(str: string): AdminEntityListItem {

        const [entity, title, ...rest] = str.split(':');
        const url = rest.join(':');
        const icon = 'browser';
        return {
            title,
            icon,
            url,
            entity,
        };
    }

    encodeEntryString(item: AdminEntityListItem): string {
        const sanitize = (str: string): string => {
            return str.replace(/:|\|/g, '');
        };
        return `${sanitize(item.entity)}:${sanitize(item.title)}:${item.url}`;
    }

    getModalFields(): FormBuilderField[] {
        const entities = Object.keys(ENTITY_HANDLER_PROVIDERS).map(x => ({ key: x, value: x }));
        return [
            ComboField('entity', this.translate('FIELDS.ENTITY'), true, entities),
            TextField('title', this.translate('FIELDS.TITLE'), true, 1, 20),
            TextField('url', this.translate('FIELDS.URL'), true, 1, 255),
        ];
    }


}

interface ScoringThresholdListItem extends ListItem {
    range: string[];
    colour: string;
}

export class ScoringThresholdsHandler extends FeatureFlagListHandler<ScoringThresholdListItem> {

    private themeEngine: ThemeEngine;

    constructor(admin: AdminEditorService) {
        super(admin, 'REPLAY-THRESHOLDS');
        this.themeEngine = new ThemeEngine();
        this.themeEngine.settingsToVariables(admin?.currentTheme?.settings);
    }

    getDefault(): ScoringThresholdListItem {
        return {
            range: ['1', '100'],
            colour: 'var(--color-success-color)',
            icon: '',
            title: '',
            iconFill: true,
        };
    }

    decodeEntryString(str: string): ScoringThresholdListItem {
        const [range, colour] = str.split(':');
        return {
            icon: 'swatch',
            iconFill: true,
            title: `${range.split(',').join(' - ')}`,
            color: this.computeColor(colour),
            range: range.split(','),
            colour,
        };
    }

    encodeEntryString(item: ScoringThresholdListItem): string {
        const sanitize = (str: string): string => {
            return str.replace(/:|\|/g, '');
        };
        return `${sanitize(`${item.range}`)}:${sanitize(item.colour)}`;
    }

    toListFieldItem(data: ScoringThresholdListItem): ListFieldValue<ScoringThresholdListItem> {
        return super.toListFieldItem({
            ...data,
            title: `${data.range.join(' - ')}`,
            color: this.computeColor(data.colour),
            icon: 'swatch',
            iconFill: true,
        });
    }

    getModalFields(): FormBuilderField[] {
        return [
            {
                id: 'range',
                title: this.translate('FIELDS.RANGE'),
                type: 'minmax',
                required: true,
                min: -999,
                max: 100,
                validate: (_field, _values, value: any[]) => {
                    for (const v of value) {
                        if (!Number.isInteger(parseFloat(v))) {
                            // range values have to be an integer otherwise we get bogged down in decimal place handling everwhere
                            return (this.translate('ERRORS.RATING_THRESHOLD_INTEGER'));
                        }
                    }
                }
            },
            this.colorComboField('colour')
        ];
    }

    private colorComboField(id: string): FormBuilderField {

        const colours: Dictionary = {
            [this.admin.translate(`EDITORS.THEME.SETTINGS.FIELDS.SUCCESS.COLOR`)]: 'var(--color-success-color)',
            [this.admin.translate(`EDITORS.THEME.SETTINGS.FIELDS.WARNING.COLOR`)]: 'var(--color-warning-color)',
            [this.admin.translate(`EDITORS.THEME.SETTINGS.FIELDS.DANGER.COLOR`)]: 'var(--color-danger-color)',
        }

        const presets = Object.keys(colours).map(key => ({ key: colours[key], value: key }));
        return {
            type: 'colorcombo',
            id: id,
            title: this.translate('FIELDS.COLOUR'),
            required: true,
            values: presets,
            getText: (field, values) => {
                const val = get(values, field.id, undefined);
                const preset = presets.find(x => x.key === val);
                const color = this.computeColor(val);
                return preset ? `${color}:${preset.value}` : color;
            },
            options: {
                computeColor: (_field: FormBuilderField, _values: any, variable: string) => {
                    return this.computeColor(variable);
                },
            },
        };
    }

    private computeColor(variable: string): string {
        return this.themeEngine.resolveFormula(variable);
    }

}